/**
 * The rest-of-today execution timeline — pure model
 * =================================================
 *
 * Constitution §6 Phase 2 item 1 (target §20/§21, invariant 21). This module
 * turns the day's rows into ONE time-ordered ledger and does nothing else.
 *
 * THE HARD RULE (target §21, SC-24): there is exactly one ordering rule here —
 * ascending start time, then end time, then the order the rows arrived in.
 * Nothing sorts, ranks, promotes, or hides by commitment, plan role, priority,
 * duration, or any other judgement. Membership and labels come from upstream:
 *
 *   - `commitment` / `plan_role` are READ off the today-view read model
 *     (orchestrator_service.ScheduledEvent), never re-derived here. A row that
 *     did not carry one shows no chip rather than a guess.
 *   - CORE / OPTIONAL is the day-open snapshot's answer (ratified decision #6).
 *     A focus task with no schedule block gets NO plane chip from this module —
 *     inferring one from FocusPlan membership would be a second engine
 *     answering the question the snapshot already owns, which is the exact
 *     defect the TodaysReleaseCard demotion was written to stop.
 *   - A row with no schedule block has NO calendar commitment (§2 dim 4 says
 *     that state is UNSCHEDULED), so `commitment` is null and no 🔓/🔒 renders.
 *
 * SC-21 (KEEP_FREE): an empty timeline is a successful outcome. This module
 * never invents a row, never pads, and never reports a shortfall.
 * SC-35: the day's end comes from the user's own sleep anchor or is unknown —
 * never a hardcoded 21:00/midnight. An unknown anchor clamps nothing.
 */

import { displayTime, parseHHMM } from '../../utils/todayOneClearThing'

export type CommitmentState = 'flexible' | 'confirmed'
export type PlanRole = 'core' | 'optional' | 'habit' | 'none'
export type RowSource = 'block' | 'focus_task' | 'habit_occurrence' | 'habit_definition'

/** One `data.scheduled_events[]` entry. The six Phase 2 keys are optional on
 *  purpose: the host maps today-view into its own prop shape, so a row that
 *  reaches us without them must render as an honest unknown, not a default. */
export interface TimelineSourceEvent {
  id: string
  item_id?: string | null
  item_type?: 'task' | 'habit' | null
  title: string
  start_time?: string
  end_time?: string
  is_all_day?: boolean
  completed?: boolean
  calendar_name?: string
  duration_minutes?: number
  commitment?: string | null
  commitment_source?: string | null
  block_source?: string | null
  plan_role?: string | null
  last_reconciled_at?: string | null
  replacement_count?: number | null
}

export interface TimelineTaskLike {
  task_id: string
  title: string
  status: string
  estimated_duration?: number
  scheduled_time_slot?: string
}

export interface TimelineOccurrenceLike {
  id: string | number
  habit_id?: string | number
  title: string
  status: string
  time_slot?: string
  duration_minutes?: number
}

export interface TimelineDefinitionLike {
  id: string | number
  title: string
  status?: string
  start_time?: string
  estimated_duration?: number
}

/**
 * The one job the page is already showing somewhere else (the hero card).
 *
 * The page filters its own task/habit feeds by id before handing them over,
 * but a SCHEDULE BLOCK for the same work arrives on a third feed it cannot
 * filter that way — so the spotlight item rendered twice on one screen, once
 * as the hero and once as a timeline row. Naming it here lets the merge claim
 * it first, using the same identity rules the de-duplication already uses.
 */
export interface TimelineSpotlightRef {
  itemType?: 'task' | 'habit' | null
  itemId?: string | number | null
  /** The exact title, for a block that carries no item linkage. */
  title?: string | null
}

export interface TimelineRow {
  /** Stable React key; unique across every source. */
  key: string
  source: RowSource
  /** The id in the row's OWN id space (block id, task id, occurrence id…). */
  refId: string
  /** Numeric schedule-block id, when this row is a real persisted block. Only
   *  these rows can carry the block verbs. */
  blockId: number | null
  /** The block's own day, when its start carries one (skip-today's `date`). */
  blockDate: string | null
  itemId: string | null
  itemType: 'task' | 'habit' | null
  title: string
  startMinutes: number | null
  endMinutes: number | null
  /** "09:00 – 10:00" / "09:00" / '' — display only. */
  timeLabel: string
  durationMinutes: number | null
  commitment: CommitmentState | null
  planRole: PlanRole
  completed: boolean
  inProgress: boolean
  /** Someone else's calendar. Horton does not offer verbs on those. */
  external: boolean
  calendarName?: string
}

export interface ExecutionTimeline {
  /** Still ahead (or running): strictly time-ordered. */
  upcoming: TimelineRow[]
  /** Already behind now: same sort, kept so nothing silently disappears. */
  earlier: TimelineRow[]
  /** No time on them at all. Input order — there is no time to sort by. */
  anytime: TimelineRow[]
  /** Minutes-from-midnight of the user's sleep anchor, or null if unknown. */
  dayEndMinutes: number | null
}

const COMMITMENT_STATES: readonly string[] = ['flexible', 'confirmed']
const PLAN_ROLES: readonly string[] = ['core', 'optional', 'habit', 'none']

/** The read model's dim-4 value, or null when the row never carried one. */
export function readCommitment(raw: unknown): CommitmentState | null {
  const v = typeof raw === 'string' ? raw.trim().toLowerCase() : ''
  return COMMITMENT_STATES.includes(v) ? (v as CommitmentState) : null
}

/** The read model's plane. Unknown and absent both mean "show no chip". */
export function readPlanRole(raw: unknown): PlanRole {
  const v = typeof raw === 'string' ? raw.trim().toLowerCase() : ''
  return PLAN_ROLES.includes(v) ? (v as PlanRole) : 'none'
}

/**
 * The numeric schedule-row id behind a timeline event id, or null.
 *
 * today-view stringifies the real row id, but falls back to a positional
 * `block-<n>` for a row that has none — that n is an array index, not an id,
 * so it must never reach an endpoint. Only digits (optionally behind the
 * `event-` prefix the schedule endpoints echo) are accepted.
 */
export function blockIdOf(eventId: string | number | null | undefined): number | null {
  if (eventId === null || eventId === undefined) return null
  const s = String(eventId).trim()
  const m = s.match(/^(?:event-)?(\d+)$/)
  if (!m) return null
  const n = Number(m[1])
  return Number.isSafeInteger(n) && n > 0 ? n : null
}

/** The calendar day an ISO start carries, or null for a bare "HH:MM". */
export function blockDateOf(startTime?: string): string | null {
  if (!startTime) return null
  const m = startTime.match(/^(\d{4}-\d{2}-\d{2})T/)
  return m ? m[1] : null
}

/** "09:00–10:00", "09:00", or '' — never a guess about a missing end.
 *  One en dash, no spaces: the same shape every other clock range on /today
 *  uses (`slotRangeLabel`, `dayShapeWords`) — North Star rule 4. */
export function timeLabelOf(startTime?: string, endTime?: string): string {
  const s = displayTime(startTime)
  if (!s) return ''
  const e = displayTime(endTime)
  return e ? `${s}–${e}` : s
}

/** Minutes-from-midnight of a "HH:MM" preference, or null. */
export function anchorMinutes(hhmm?: string | null): number | null {
  return parseHHMM(hhmm ?? undefined)
}

/** The second half of a bare "HH:MM-HH:MM" slot. ISO datetimes carry '-' in
 *  their date, so they are never split. */
function slotEnd(slot?: string): string | undefined {
  if (!slot || slot.includes('T')) return undefined
  const parts = slot.split('-')
  return parts.length > 1 ? parts[1].trim() : undefined
}

/** "09:00 – 10:00" from a slot string, "09:00" from a bare start. */
export function slotLabel(slot?: string): string {
  return timeLabelOf(slot, slotEnd(slot))
}

function titleKey(title: string): string {
  return `title:${title.trim().toLowerCase()}`
}

function itemKey(itemType?: string | null, itemId?: string | number | null): string | null {
  if (itemId === null || itemId === undefined || itemId === '') return null
  const t = String(itemType || '').trim().toLowerCase()
  if (t !== 'task' && t !== 'habit') return null
  return `${t}:${itemId}`
}

/**
 * Merge the day's rows into one ledger and time-sort it.
 *
 * Sources, in the order they are CONSIDERED (which is not an order they are
 * RENDERED in — everything is re-sorted by time at the end):
 *   1. schedule blocks (the only rows carrying a commitment and a plane),
 *   2. focus tasks the blocks did not already account for,
 *   3. habit occurrences, then habit definitions, likewise.
 *
 * De-duplication is identity-only: a canonical `type:id` ref, or the user's own
 * exact title when a block carries no linkage (which is the same fallback
 * today-view itself uses to attach an unlinked block to its task). Two distinct
 * items sharing one exact title collapse to one row; that is the accepted cost
 * of not showing the same work twice, and it is a string identity check, never
 * an interpretation of what the words mean (SC-27).
 *
 * `spotlight` is the same identity check applied to the hero card: the item the
 * page is ALREADY showing above the timeline is claimed before any source is
 * walked, so it cannot also appear as a row. Note the asymmetry — two blocks
 * that merely share a title are BOTH kept (they are two real reservations on
 * one clock), while the spotlight is removed from every feed, because there the
 * duplicate is on the same screen.
 */
export function buildExecutionTimeline(input: {
  events: TimelineSourceEvent[]
  tasks?: TimelineTaskLike[]
  habitOccurrences?: TimelineOccurrenceLike[]
  habitDefinitions?: TimelineDefinitionLike[]
  nowMinutes: number
  dayEndMinutes?: number | null
  spotlight?: TimelineSpotlightRef | null
}): ExecutionTimeline {
  const { events, nowMinutes } = input
  const tasks = input.tasks ?? []
  const occurrences = input.habitOccurrences ?? []
  const definitions = input.habitDefinitions ?? []
  const dayEndMinutes = input.dayEndMinutes ?? null

  const seen = new Set<string>()
  const rows: TimelineRow[] = []

  // The hero's identity, in the same key space the de-duplication uses. Held
  // apart from `seen` on purpose: `seen` grows as rows are added (so it must
  // never suppress a second real block), while this set is fixed for the run.
  const spotlit = new Set<string>()
  if (input.spotlight) {
    const s = input.spotlight
    const k = itemKey(s.itemType, s.itemId)
    if (k) spotlit.add(k)
    if (s.title && s.title.trim()) spotlit.add(titleKey(s.title))
  }
  const isSpotlit = (...keys: Array<string | null>) =>
    keys.some(k => !!k && spotlit.has(k))

  const claim = (...keys: Array<string | null>) => {
    for (const k of keys) if (k) seen.add(k)
  }
  const taken = (...keys: Array<string | null>) => keys.some(k => !!k && seen.has(k))

  for (const ev of events) {
    // The hero already shows this work. A block for it is the SAME job, so it
    // is dropped rather than drawn a second time three inches lower.
    if (isSpotlit(itemKey(ev.item_type, ev.item_id), titleKey(ev.title))) continue
    const start = ev.is_all_day ? null : parseHHMM(ev.start_time)
    const end = ev.is_all_day ? null : parseHHMM(ev.end_time)
    const external = ev.block_source === 'external' || !!ev.calendar_name
    rows.push({
      key: `block-${ev.id}`,
      source: 'block',
      refId: String(ev.id),
      blockId: external ? null : blockIdOf(ev.id),
      blockDate: blockDateOf(ev.start_time),
      itemId: ev.item_id ?? null,
      itemType: ev.item_type ?? null,
      title: ev.title,
      startMinutes: start,
      endMinutes: end,
      timeLabel: ev.is_all_day ? '' : timeLabelOf(ev.start_time, ev.end_time),
      durationMinutes: ev.duration_minutes ?? (start !== null && end !== null && end > start ? end - start : null),
      commitment: readCommitment(ev.commitment),
      planRole: readPlanRole(ev.plan_role),
      completed: !!ev.completed,
      inProgress: false,
      external,
      calendarName: ev.calendar_name,
    })
    claim(itemKey(ev.item_type, ev.item_id), titleKey(ev.title))
  }

  for (const t of tasks) {
    if (t.status === 'completed') continue
    if (isSpotlit(itemKey('task', t.task_id), titleKey(t.title))) continue
    if (taken(itemKey('task', t.task_id), titleKey(t.title))) continue
    const start = parseHHMM(t.scheduled_time_slot)
    const end = parseHHMM(slotEnd(t.scheduled_time_slot))
    rows.push({
      key: `task-${t.task_id}`,
      source: 'focus_task',
      refId: String(t.task_id),
      blockId: null,
      blockDate: null,
      itemId: String(t.task_id),
      itemType: 'task',
      title: t.title,
      startMinutes: start,
      endMinutes: end,
      timeLabel: start === null ? '' : slotLabel(t.scheduled_time_slot),
      durationMinutes: t.estimated_duration ?? null,
      // No block, so no calendar reservation exists to name (§2 dim 4).
      commitment: null,
      planRole: 'none',
      completed: false,
      inProgress: t.status === 'in_progress',
      external: false,
    })
    claim(itemKey('task', t.task_id), titleKey(t.title))
  }

  for (const o of occurrences) {
    if (o.status === 'completed') continue
    // Occurrence ids and definition ids are separate id spaces, so the title is
    // what catches a hero OCCURRENCE against its own DEFINITION row (and the
    // reverse) — the second way the same habit used to render twice.
    if (isSpotlit(itemKey('habit', o.habit_id ?? o.id), titleKey(o.title))) continue
    if (taken(itemKey('habit', o.habit_id ?? o.id), titleKey(o.title))) continue
    const start = parseHHMM(o.time_slot)
    const end = parseHHMM(slotEnd(o.time_slot))
    rows.push({
      key: `occ-${o.id}`,
      source: 'habit_occurrence',
      refId: String(o.id),
      blockId: null,
      blockDate: null,
      itemId: o.habit_id !== undefined && o.habit_id !== null ? String(o.habit_id) : null,
      itemType: 'habit',
      title: o.title,
      startMinutes: start,
      endMinutes: end,
      timeLabel: start === null ? '' : slotLabel(o.time_slot),
      durationMinutes: o.duration_minutes ?? null,
      commitment: null,
      // A habit occurrence IS its own plane — the same rule today-view applies
      // before it ever consults CORE/OPTIONAL membership (SC-23).
      planRole: 'habit',
      completed: false,
      inProgress: false,
      external: false,
    })
    claim(itemKey('habit', o.habit_id ?? o.id), titleKey(o.title))
  }

  for (const d of definitions) {
    if (d.status === 'completed') continue
    if (isSpotlit(itemKey('task', d.id), itemKey('habit', d.id), titleKey(d.title))) continue
    if (taken(itemKey('task', d.id), itemKey('habit', d.id), titleKey(d.title))) continue
    const start = parseHHMM(d.start_time)
    rows.push({
      key: `def-${d.id}`,
      source: 'habit_definition',
      refId: String(d.id),
      blockId: null,
      blockDate: null,
      itemId: String(d.id),
      itemType: 'habit',
      title: d.title,
      startMinutes: start,
      endMinutes: null,
      timeLabel: start === null ? '' : slotLabel(d.start_time),
      durationMinutes: d.estimated_duration ?? null,
      commitment: null,
      planRole: 'habit',
      completed: false,
      inProgress: false,
      external: false,
    })
    claim(itemKey('task', d.id), itemKey('habit', d.id), titleKey(d.title))
  }

  // THE ONE SORT. Ascending start, then end, then arrival order — a total
  // order over time alone. Adding any other term to this comparator is the
  // defect this module exists to prevent.
  const ordered = rows
    .map((row, index) => ({ row, index }))
    .filter(x => x.row.startMinutes !== null)
    .sort((a, b) => {
      const s = (a.row.startMinutes as number) - (b.row.startMinutes as number)
      if (s !== 0) return s
      const ae = a.row.endMinutes ?? a.row.startMinutes ?? 0
      const be = b.row.endMinutes ?? b.row.startMinutes ?? 0
      if (ae !== be) return ae - be
      return a.index - b.index
    })
    .map(x => x.row)

  const upcoming: TimelineRow[] = []
  const earlier: TimelineRow[] = []
  for (const row of ordered) {
    const tail = row.endMinutes ?? row.startMinutes ?? 0
    if (tail > nowMinutes) upcoming.push(row)
    else earlier.push(row)
  }

  const anytime = rows.filter(r => r.startMinutes === null && !r.completed)

  return { upcoming, earlier, anytime, dayEndMinutes }
}
